import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../context/AuthContext';
import Swal from 'sweetalert2';

const getInitialConsents = () => {
  try {
    const stored = sessionStorage.getItem('consentsAcceptedAtLogin');
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return {
      termsAndConditions: parsed.termsAndConditions || { accepted: false, acceptedAt: null },
      privacyPolicy: parsed.privacyPolicy || { accepted: false, acceptedAt: null }
    };
  } catch {
    return null;
  }
};

const UserForm = () => {
  const { user, submitUserData } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: user?.email || '',
    phone: user?.phoneNumber || '',
    ageRange: '',
    gender: '',
    consents: {
      termsAndConditions: { accepted: false, acceptedAt: null },
      privacyPolicy: { accepted: false, acceptedAt: null },
      marketing: { accepted: false, acceptedAt: null }
    }
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [domainConfig, setDomainConfig] = useState(null);
  const injectedScriptRefs = useRef([]);

  useEffect(() => {
    const fromLogin = getInitialConsents();
    if (fromLogin) {
      setFormData(prev => ({
        ...prev,
        consents: { ...prev.consents, ...fromLogin }
      }));
      sessionStorage.removeItem('consentsAcceptedAtLogin');
    }
  }, []);

  useEffect(() => {
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    fetch(`/api/domain-config?host=${encodeURIComponent(host)}`)
      .then(res => (res.ok ? res.json().catch(() => null) : null))
      .then(data => {
        if (!data || typeof data !== 'object') return;
        try {
          setDomainConfig(data);
          const root = document.documentElement?.style;
          if (root && data.colors && typeof data.colors === 'object') {
            if (data.colors.primary) root.setProperty('--primary-color', data.colors.primary);
            if (data.colors.primaryHover) root.setProperty('--primary-hover', data.colors.primaryHover);
            if (data.colors.background) root.setProperty('--background', data.colors.background);
            if (data.colors.surface) root.setProperty('--surface', data.colors.surface);
            if (data.colors.textPrimary) root.setProperty('--text-primary', data.colors.textPrimary);
            if (data.colors.textSecondary) root.setProperty('--text-secondary', data.colors.textSecondary);
          }
          const snippets = Array.isArray(data.snippets) ? data.snippets : [];
          snippets.forEach(s => {
            if (!s || s.tag !== 'script' || !s.src) return;
            try {
              const el = document.createElement('script');
              el.src = s.src;
              if (s.async) el.async = true;
              if (s.defer) el.defer = true;
              if (typeof s.type === 'string' && s.type) el.type = s.type;
              if (s['data-id']) el.setAttribute('data-id', s['data-id']);
              document.head.appendChild(el);
              injectedScriptRefs.current.push(el);
            } catch (_) {}
          });
        } catch (_) {}
      })
      .catch(() => {});
    return () => {
      injectedScriptRefs.current.forEach(el => {
        try {
          el.remove();
        } catch (_) {}
      });
      injectedScriptRefs.current = [];
    };
  }, []);

  const handleChange = e => {
    const { name, value, checked } = e.target;
    if (name.startsWith('consents.')) {
      const consentType = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        consents: {
          ...prev.consents,
          [consentType]: {
            accepted: checked,
            acceptedAt: checked ? new Date() : null
          }
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'El nombre es obligatorio';
    if (!formData.email.trim()) newErrors.email = 'El email es obligatorio';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'El email no es válido';
    if (!formData.phone.trim()) newErrors.phone = 'El teléfono es obligatorio';
    if (!formData.ageRange) newErrors.ageRange = 'Selecciona tu rango de edad';
    if (!formData.gender) newErrors.gender = 'Selecciona tu género';
    if (!formData.consents.termsAndConditions.accepted)
      newErrors.termsAndConditions = 'Debes aceptar los términos y condiciones';
    if (!formData.consents.privacyPolicy.accepted)
      newErrors.privacyPolicy = 'Debes aceptar la política de privacidad';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      await submitUserData(formData);
    } catch (error) {
      console.error('Error submitting user data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Hubo un error al guardar tu perfil. Por favor, intenta de nuevo.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageOverlay>
      <ProfileCard>
        <CardHeader>
          <CardTitle>
            <TitleIcon>👤</TitleIcon>
            Completa tu perfil
          </CardTitle>
          <CardSubtitle>Unos datos más y podrás usar tu cuenta</CardSubtitle>
        </CardHeader>

        <Form onSubmit={handleSubmit}>
          <FormSection>
            <SectionTitle>Información personal</SectionTitle>

            <FormGroup>
              <FormLabel>
                Nombre completo <RequiredStar>*</RequiredStar>
              </FormLabel>
              <InputWrapper>
                <FormInput
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Tu nombre completo"
                  $hasError={!!errors.name}
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <FormLabel>
                Email <RequiredStar>*</RequiredStar>
              </FormLabel>
              <InputWrapper>
                <FormInput
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="tu@email.com"
                  $hasError={!!errors.email}
                />
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <FormLabel>Teléfono</FormLabel>
              <InputWrapper>
                <FormInput
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  readOnly
                  $disabled
                />
                <FormHelp>Número verificado en el inicio de sesión</FormHelp>
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <FormLabel>
                Rango de edad <RequiredStar>*</RequiredStar>
              </FormLabel>
              <InputWrapper>
                <FormSelect
                  name="ageRange"
                  value={formData.ageRange}
                  onChange={handleChange}
                  $hasError={!!errors.ageRange}
                >
                  <option value="">Selecciona tu edad</option>
                  <option value="18-24">18-24 años</option>
                  <option value="25-34">25-34 años</option>
                  <option value="35-44">35-44 años</option>
                  <option value="45-54">45-54 años</option>
                  <option value="55-64">55-64 años</option>
                  <option value="65+">65+ años</option>
                </FormSelect>
                {errors.ageRange && <ErrorMessage>{errors.ageRange}</ErrorMessage>}
              </InputWrapper>
            </FormGroup>

            <FormGroup $noMargin>
              <FormLabel>
                Género <RequiredStar>*</RequiredStar>
              </FormLabel>
              <InputWrapper>
                <FormSelect
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  $hasError={!!errors.gender}
                >
                  <option value="">Selecciona tu género</option>
                  <option value="male">Hombre</option>
                  <option value="female">Mujer</option>
                  <option value="not_specified">No especificar</option>
                </FormSelect>
                {errors.gender && <ErrorMessage>{errors.gender}</ErrorMessage>}
              </InputWrapper>
            </FormGroup>
          </FormSection>

          <FormSection>
            <SectionTitle>Consentimientos</SectionTitle>

            <ConsentRow $hasError={!!errors.termsAndConditions}>
              <Checkbox
                type="checkbox"
                name="consents.termsAndConditions"
                checked={formData.consents.termsAndConditions.accepted}
                onChange={handleChange}
                id="terms"
              />
              <ConsentLabel htmlFor="terms">
                Acepto los{' '}
                {domainConfig?.termsUrl ? (
                  <InlineLink href={domainConfig.termsUrl} target="_blank" rel="noopener noreferrer">
                    términos y condiciones
                  </InlineLink>
                ) : (
                  <InlineLink as="span">términos y condiciones</InlineLink>
                )}{' '}
                <RequiredStar>*</RequiredStar>
              </ConsentLabel>
            </ConsentRow>
            {errors.termsAndConditions && (
              <ErrorMessage $block>{errors.termsAndConditions}</ErrorMessage>
            )}

            <ConsentRow $hasError={!!errors.privacyPolicy}>
              <Checkbox
                type="checkbox"
                name="consents.privacyPolicy"
                checked={formData.consents.privacyPolicy.accepted}
                onChange={handleChange}
                id="privacy"
              />
              <ConsentLabel htmlFor="privacy">
                Acepto la{' '}
                {domainConfig?.privacyUrl ? (
                  <InlineLink href={domainConfig.privacyUrl} target="_blank" rel="noopener noreferrer">
                    política de privacidad
                  </InlineLink>
                ) : (
                  <InlineLink as="span">política de privacidad</InlineLink>
                )}{' '}
                <RequiredStar>*</RequiredStar>
              </ConsentLabel>
            </ConsentRow>
            {errors.privacyPolicy && <ErrorMessage $block>{errors.privacyPolicy}</ErrorMessage>}

            <ConsentRow>
              <Checkbox
                type="checkbox"
                name="consents.marketing"
                checked={formData.consents.marketing.accepted}
                onChange={handleChange}
                id="marketing"
              />
              <ConsentLabel htmlFor="marketing">
                Quiero recibir comunicaciones promocionales y ofertas especiales
              </ConsentLabel>
            </ConsentRow>
          </FormSection>

          <ButtonGroup>
            <SubmitButton type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner />
                  Guardando...
                </>
              ) : (
                'Completar perfil'
              )}
            </SubmitButton>
          </ButtonGroup>
        </Form>
      </ProfileCard>
    </PageOverlay>
  );
};

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-16px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const PageOverlay = styled.div`
  min-height: 100vh;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1rem;
  animation: ${fadeIn} 0.35s ease-out;
  backdrop-filter: blur(6px);
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(118, 75, 162, 0.08) 100%),
    var(--background);
`;

const ProfileCard = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 2.5rem;
  width: 100%;
  max-width: 600px;
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.12);
  animation: ${slideIn} 0.4s ease-out;
  border: 1px solid rgba(0, 0, 0, 0.06);
`;

const CardHeader = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 1.25rem;
  border-bottom: 2px solid #f0f0f0;
`;

const CardTitle = styled.h1`
  color: #333;
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TitleIcon = styled.span`
  font-size: 1.5rem;
`;

const CardSubtitle = styled.p`
  color: #6c757d;
  font-size: 0.95rem;
  margin: 0;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
`;

const FormSection = styled.div`
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 12px;
  border: 1px solid #e9ecef;
`;

const SectionTitle = styled.h2`
  color: #495057;
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 1.25rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: '';
    width: 4px;
    height: 20px;
    background: linear-gradient(135deg, var(--primary-color, #667eea) 0%, #764ba2 100%);
    border-radius: 2px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: ${p => (p.$noMargin ? 0 : '1.25rem')};
`;

const FormLabel = styled.label`
  color: #333;
  font-weight: 600;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const RequiredStar = styled.span`
  color: #e74c3c;
  font-weight: bold;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const FormInput = styled.input`
  padding: 1rem;
  border: 2px solid
    ${p => (p.$hasError ? '#e74c3c' : p.$disabled ? '#e9ecef' : '#ddd')};
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: ${p => (p.$disabled ? '#f8f9fa' : '#fff')};
  color: ${p => (p.$disabled ? '#6c757d' : '#333')};
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--primary-color, #667eea);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.12);
  }

  &:read-only,
  &:disabled {
    cursor: not-allowed;
  }

  &::placeholder {
    color: #adb5bd;
  }
`;

const FormSelect = styled.select`
  padding: 1rem;
  border: 2px solid ${p => (p.$hasError ? '#e74c3c' : '#ddd')};
  border-radius: 10px;
  font-size: 1rem;
  background: #fff;
  transition: all 0.3s ease;
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.75rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
  color: #333;

  &:focus {
    outline: none;
    border-color: var(--primary-color, #667eea);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.12);
  }

  &:hover {
    border-color: #adb5bd;
    background-color: #fafafa;
  }

  ${p =>
    p.$hasError &&
    `
    background-color: #fdf2f2;
    &:focus {
      border-color: #e74c3c;
      box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.1);
    }
  `}
`;

const FormHelp = styled.small`
  color: #6c757d;
  font-size: 0.8rem;
  font-style: italic;
  margin-top: 0.25rem;
  display: block;
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  font-weight: 500;
  animation: ${fadeIn} 0.3s ease-out;
  ${p => p.$block && 'margin-bottom: 0.5rem;'}
`;

const ConsentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  margin-bottom: 0.75rem;
  background: ${p => (p.$hasError ? '#fdf2f2' : '#fff')};
  border: 1px solid ${p => (p.$hasError ? '#f5c6cb' : '#e9ecef')};
`;

const Checkbox = styled.input`
  margin-top: 0.2rem;
  width: 18px;
  height: 18px;
  accent-color: var(--primary-color, #667eea);
  flex-shrink: 0;
`;

const ConsentLabel = styled.label`
  color: #333;
  font-size: 0.9rem;
  line-height: 1.45;
  cursor: pointer;
  flex: 1;
`;

const InlineLink = styled.a`
  color: var(--primary-color, #667eea);
  font-weight: 600;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.25rem;
`;

const SubmitButton = styled.button`
  flex: 1;
  background: var(--primary-color);
  color: var(--text-light);
  padding: 1rem 2rem;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: var(--transition);
  font-weight: 600;
  border: none;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 48px;

  &:hover:not(:disabled) {
    background: var(--primary-hover);
    transform: translateY(-1px);
    box-shadow: var(--shadow-lg);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
  }
`;

const Spinner = styled.span`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

export default UserForm;
